import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, FileTextIcon, CheckIcon, FolderIcon } from 'lucide-react';
import { Condition, ConditionGroup } from '../types';
interface DocumentAssignmentModalProps {
  isOpen: boolean;
  conditions: Condition[];
  groups: ConditionGroup[];
  onClose: () => void;
  onSubmit: (documentName: string, conditionIds: string[]) => void;
}
export function DocumentAssignmentModal({
  isOpen,
  conditions,
  groups,
  onClose,
  onSubmit
}: DocumentAssignmentModalProps) {
  const [documentName, setDocumentName] = useState('');
  const [selectedConditions, setSelectedConditions] = useState<Set<string>>(new Set());
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (isOpen) {
      setDocumentName('');
      setSelectedConditions(new Set());
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);
  const toggleCondition = (conditionId: string) => {
    setSelectedConditions(prev => {
      const next = new Set(prev);
      if (next.has(conditionId)) {
        next.delete(conditionId);
      } else {
        next.add(conditionId);
      }
      return next;
    });
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (documentName.trim() && selectedConditions.size > 0) {
      onSubmit(documentName.trim(), Array.from(selectedConditions));
      onClose();
    }
  };
  const unclearedConditions = conditions.filter(c => c.status !== 'cleared');
  // Group conditions for display
  const groupedConditions = groups.map(g => ({
    group: g,
    conditions: unclearedConditions.filter(c => c.groupId === g.id)
  })).filter(g => g.conditions.length > 0);
  const ungroupedConditions = unclearedConditions.filter(c => !c.groupId);
  return <AnimatePresence data-id="element-279">
      {isOpen && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-280">
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={onClose} data-id="element-281" />

          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 10
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 10
      }} className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col overflow-hidden" data-id="element-282">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100" data-id="element-283">
              <div className="flex items-center gap-3" data-id="element-284">
                <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center" data-id="element-285">
                  <FileTextIcon className="w-5 h-5 text-blue-600" data-id="element-286" />
                </div>
                <div data-id="element-287">
                  <h2 className="font-semibold text-slate-900" data-id="element-288">
                    Upload Document
                  </h2>
                  <p className="text-sm text-slate-500" data-id="element-289">
                    Select conditions this document satisfies
                  </p>
                </div>
              </div>
              <button onClick={onClose} className="p-2 text-slate-400 hover:bg-slate-100 rounded-lg" data-id="element-290">
                <XIcon className="w-5 h-5" data-id="element-291" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden" data-id="element-292">
              <div className="px-6 py-4 border-b border-slate-100" data-id="element-293">
                <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-294">
                  Document Name
                </label>
                <input ref={inputRef} type="text" value={documentName} onChange={e => setDocumentName(e.target.value)} placeholder="e.g., Bank Statement.pdf" className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-blue-500 outline-none" data-id="element-295" />
              </div>

              <div className="flex-1 overflow-y-auto px-6 py-4" data-id="element-296">
                {unclearedConditions.length === 0 ? <div className="text-center py-8 text-slate-500 text-sm" data-id="element-297">
                    All conditions are already cleared!
                  </div> : <div className="space-y-6" data-id="element-298">
                    {/* Select All / Deselect All */}
                    <div className="flex items-center justify-between pb-3 border-b border-slate-100" data-id="element-299">
                      <span className="text-sm text-slate-600" data-id="element-300">
                        {unclearedConditions.length} outstanding condition
                        {unclearedConditions.length !== 1 ? 's' : ''}
                      </span>
                      <button type="button" onClick={() => {
                  if (selectedConditions.size === unclearedConditions.length) {
                    setSelectedConditions(new Set());
                  } else {
                    setSelectedConditions(new Set(unclearedConditions.map(c => c.id)));
                  }
                }} className="text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors" data-id="element-301">
                        {selectedConditions.size === unclearedConditions.length ? 'Deselect All' : 'Select All'}
                      </button>
                    </div>
                    {ungroupedConditions.length > 0 && <div data-id="element-302">
                        <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2" data-id="element-303">
                          Ungrouped Conditions
                        </h4>
                        <div className="space-y-1" data-id="element-304">
                          {ungroupedConditions.map(c => <ConditionCheckbox key={c.id} condition={c} selected={selectedConditions.has(c.id)} onToggle={() => toggleCondition(c.id)} data-id="element-305" />)}
                        </div>
                      </div>}

                    {groupedConditions.map(({
                group,
                conditions: groupConds
              }) => {
                const allSelected = groupConds.every(c => selectedConditions.has(c.id));
                const someSelected = !allSelected && groupConds.some(c => selectedConditions.has(c.id));
                const toggleGroup = () => {
                  setSelectedConditions(prev => {
                    const next = new Set(prev);
                    if (allSelected) {
                      groupConds.forEach(c => next.delete(c.id));
                    } else {
                      groupConds.forEach(c => next.add(c.id));
                    }
                    return next;
                  });
                };
                const selectedCount = groupConds.filter(c => selectedConditions.has(c.id)).length;
                return <div key={group.id} data-id="element-306">
                            <button type="button" onClick={toggleGroup} className="flex items-center gap-2.5 w-full py-2 px-2 rounded-md hover:bg-slate-50 transition-colors mb-1" data-id="element-307">
                              <div className="relative" data-id="element-308">
                                <div className={`w-4 h-4 rounded border flex items-center justify-center ${allSelected ? 'bg-blue-600 border-blue-600' : someSelected ? 'bg-blue-100 border-blue-400' : 'border-slate-300'}`} data-id="element-309">
                                  {allSelected && <CheckIcon className="w-3 h-3 text-white" data-id="element-310" />}
                                  {someSelected && <div className="w-2 h-0.5 bg-blue-500 rounded" data-id="element-311" />}
                                </div>
                              </div>
                              <FolderIcon className="w-3.5 h-3.5" style={{
                      color: '#3b82f6'
                    }} data-id="element-312" />
                              <span className="text-xs font-semibold text-slate-700 uppercase tracking-wider" data-id="element-313">
                                {group.note || group.name}
                              </span>
                              <span className="text-[10px] font-medium text-slate-400 ml-auto" data-id="element-314">
                                {selectedCount}/{groupConds.length}
                              </span>
                            </button>
                            <div className="space-y-1 ml-2 border-l-2 border-slate-100 pl-2" data-id="element-315">
                              {groupConds.map(c => <ConditionCheckbox key={c.id} condition={c} selected={selectedConditions.has(c.id)} onToggle={() => toggleCondition(c.id)} data-id="element-316" />)}
                            </div>
                          </div>;
              })}
                  </div>}
              </div>

              <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 flex items-center justify-between" data-id="element-317">
                <span className="text-sm text-slate-600 font-medium" data-id="element-318">
                  {selectedConditions.size} selected
                </span>
                <div className="flex gap-2" data-id="element-319">
                  <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-200 rounded-md" data-id="element-320">
                    Cancel
                  </button>
                  <button type="submit" disabled={!documentName.trim() || selectedConditions.size === 0} className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50" data-id="element-321">
                    Upload & Clear
                  </button>
                </div>
              </div>
            </form>
          </motion.div>
        </div>}
    </AnimatePresence>;
}
function ConditionCheckbox({
  condition,
  selected,
  onToggle
}: {
  condition: Condition;
  selected: boolean;
  onToggle: () => void;
}) {
  return <label className="flex items-start gap-3 py-2 px-2 rounded hover:bg-slate-50 cursor-pointer group" data-id="element-322">
      <div className="relative mt-0.5" data-id="element-323">
        <input type="checkbox" checked={selected} onChange={onToggle} className="sr-only" data-id="element-324" />
        <div className={`w-4 h-4 rounded border flex items-center justify-center ${selected ? 'bg-blue-600 border-blue-600' : 'border-slate-300 group-hover:border-blue-400'}`} data-id="element-325">
          {selected && <CheckIcon className="w-3 h-3 text-white" data-id="element-326" />}
        </div>
      </div>
      <div data-id="element-327">
        <div className="text-sm font-medium text-slate-800" data-id="element-328">
          {condition.name}
        </div>
        {condition.description && <div className="text-xs text-slate-500 line-clamp-1" data-id="element-329">
            {condition.description}
          </div>}
      </div>
    </label>;
}